import React, { useContext, useEffect, useState, useReducer } from 'react'
import { NavLink, useParams, useHistory } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'

import { IManualExpense, IOrganization } from 'types'
import ScreenLoading from 'components/ScreenLoading'
import InputField from 'components/InputField'
import DropDownField from 'components/DropDownField'
import AutoCompleteField from 'components/AutoCompleteField'
import OrganizationsContext from 'containers/organizations/organizationsContext'
import useGetCurrencies from 'containers/currencies/useGetCurrencies'
import useGetManualExpense from 'containers/expenses/useGetManualExpense'
import useSaveManualExpense from 'containers/expenses/useSaveManualExpense'
import useListManualExpenseCategories from 'containers/expenses/useListManualExpenseCategories'
import useListManualExpenseSubcategories from 'containers/expenses/useListManualExpenseSubcategories'

import {
  useValidator,
  isRequired,
  maxLength,
  isValidNumber,
} from 'containers/formValidator'

import './ManualExpensesList.scss'

type ExpenseAction =
  | { type: 'organizationLoaded'; payload: IOrganization }
  | { type: 'expenseLoaded'; payload: IManualExpense }
  | { type: 'paymentDateChanged'; payload: string }
  | { type: 'amountChanged'; payload: number }
  | { type: 'categoryChanged'; payload: string }
  | { type: 'currencyCodeChanged'; payload: string }
  | { type: 'subcategoryChanged'; payload: string }
  | { type: 'startDateChanged'; payload: string }
  | { type: 'endDateChanged'; payload: string }
  | { type: 'isMarketingRelatedChanged'; payload: boolean }
  | { type: 'descriptionChanged'; payload: string }

const expenseReducer = (
  expense: IManualExpense,
  action: ExpenseAction
): IManualExpense => {
  switch (action.type) {
    case 'organizationLoaded':
      if (action.payload.currency_id) {
        return { ...expense, currencyCode: action.payload.currency_id }
      } else {
        return expense
      }
    case 'expenseLoaded':
      return action.payload
    case 'paymentDateChanged':
      return { ...expense, paymentDate: action.payload }
    case 'amountChanged':
      return { ...expense, amount: action.payload }
    case 'categoryChanged':
      return { ...expense, category: action.payload }
    case 'subcategoryChanged':
      return { ...expense, subcategory: action.payload }
    case 'currencyCodeChanged':
      return { ...expense, currencyCode: action.payload }
    case 'startDateChanged':
      return { ...expense, startDate: action.payload }
    case 'endDateChanged':
      return { ...expense, endDate: action.payload }
    case 'isMarketingRelatedChanged':
      return { ...expense, isMarketingRelated: action.payload }
    case 'descriptionChanged':
      return { ...expense, description: action.payload }
  }
}

const ManualExpenseView = () => {
  const { expenseId } = useParams<{ expenseId: string }>()
  const { currentOrganization, isLoadingOrganization } =
    useContext(OrganizationsContext)
  const currentOrganizationId = currentOrganization.id

  const history = useHistory()

  const [expense, dispatch] = useReducer(expenseReducer, {
    paymentDate: '',
    uuid: expenseId,
    description: '',
    amount: 0,
    currencyCode: currentOrganization?.currency_id || 'USD',
    category: '',
    subcategory: '',
    startDate: '',
    endDate: '',
    isMarketingRelated: true,
  })

  const [
    currencies,
    fetchCurrencies,
    fetchCurrenciesStatus,
    fetchCurrenciesError,
  ] = useGetCurrencies()
  const [_expense, fetchExpense, fetchExpenseStatus, fetchExpenseError] =
    useGetManualExpense(currentOrganization.id, expenseId)
  const [saveExpense, saveExpenseStatus, saveExpenseError] =
    useSaveManualExpense(currentOrganizationId)
  const [manualExpenseCategories, fetchManualExpenseCategories] =
    useListManualExpenseCategories(currentOrganization.id)
  const [manualExpenseSubcategories, fetchManualExpenseSubcategories] =
    useListManualExpenseSubcategories(currentOrganization.id)

  const [advancedOpen, setAdvancedOpen] = useState(false)
  // Raw Amount text as typed. Rendering straight from the parsed cents ate
  // any keystroke that doesn't parse (letters cleared the field, "12." lost
  // its dot so decimals were untypeable). null = not edited yet, show stored.
  const [amountText, setAmountText] = useState<string | null>(null)

  const [validateForm, getFormFieldError] = useValidator({
    amount: [isValidNumber('Invalid amount'), isRequired()],
    paymentDate: [isRequired()],
    category: [isRequired()],
    subcategory: [isRequired()],
    description: [maxLength(50, 'Description too long')],
    // Same rule as the API: maya-etl spreads the amount over the period only
    // when both dates are set and end is after start. Otherwise most of the
    // amount silently disappears from reports (#1792). Dates are YYYY-MM-DD,
    // so string comparison orders them.
    startDate: [
      (start, e: IManualExpense) =>
        !start && e.endDate
          ? 'Set a start date too, or clear the end date'
          : '',
    ],
    endDate: [
      (end, e: IManualExpense) => {
        if (!end) {
          return e.startDate
            ? 'Set an end date too, or clear the start date'
            : ''
        }
        return e.startDate && end <= e.startDate
          ? 'Must be after the start date. For a single day, leave the period empty.'
          : ''
      },
    ],
  })

  const getData = async () => {
    if (currentOrganization.id && expenseId) {
      fetchManualExpenseCategories()
      fetchManualExpenseSubcategories()
      const expense = await fetchExpense()
      if (!expense) {
        return
      }
      dispatch({ type: 'expenseLoaded', payload: expense })
      // The loaded expense replaces any amount typed while the fetch was in
      // flight — drop the raw text so the field shows the stored value.
      setAmountText(null)
    }
  }

  useEffect(() => {
    if (!isLoadingOrganization) {
      dispatch({ type: 'organizationLoaded', payload: currentOrganization })
    }
    // eslint-disable-next-line
  }, [isLoadingOrganization])

  useEffect(() => {
    fetchCurrencies()
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    getData()
    // eslint-disable-next-line
  }, [currentOrganizationId, expenseId])

  const handleSave = async () => {
    if (!validateForm(expense)) {
      // The period fields sit in the collapsed Advanced section; open it so
      // their errors are visible.
      if (expense.startDate || expense.endDate) {
        setAdvancedOpen(true)
      }
      return
    }
    if (currentOrganizationId) {
      // useMutator resolves to null on failure instead of throwing, so the
      // result is the only success signal. Navigating unconditionally showed
      // the "saved" banner for expenses the API had rejected (#1792).
      const result = await saveExpense(expense)
      if (!result?.success) {
        return
      }
      localStorage.setItem('manualExpenseSaved', '1')
      history.push(`/organization/${currentOrganization.slug}/expenses`)
    }
  }

  const loadError = [fetchExpenseError, fetchCurrenciesError].find((e) => e)

  return (
    <div className="clusters-campaigns container-column manual-expenses">
      {loadError && (
        <div className="alert alert-danger" role="alert">
          {loadError}
        </div>
      )}
      <NavLink to={`/organization/${currentOrganization.slug}/expenses`}>
        <span className="material-icons-outlined back-arrow non-selectable">
          arrow_back
        </span>
      </NavLink>
      <div className="head" style={{ marginBottom: '30px' }}>
        <div className="page-name">
          <h2>Manual Expenses</h2>
          <span className="subtitle-text">
            Record expenses that you want included in your reports, such as
            non-digital marketing expenses.
          </span>
        </div>
      </div>
      <div
        className={`grid manual-expenses-form ${
          fetchExpenseStatus === 'pending' ? 'form-loading' : ''
        }`}
      >
        <div className="form-container grid-column-12 g-col-xxl-6">
          <div className="loading-indicator">
            <ScreenLoading />
          </div>
          <div className="loading-shade"></div>
          <div className="form-body">
            <div className="grid">
              <div className="grid-column-12">
                <InputField
                  type="date"
                  label="Payment Date"
                  value={expense.paymentDate}
                  setValue={(v) =>
                    dispatch({ type: 'paymentDateChanged', payload: v })
                  }
                  required
                  error={getFormFieldError('paymentDate')}
                  helperText="The invoice or payment date. Without a period below, the whole amount is reported on this day."
                />
              </div>
            </div>
            <div className="grid">
              <div className="grid-column-12 g-col-lg-8">
                <InputField
                  label="Amount"
                  type="text"
                  value={
                    amountText ??
                    (expense.amount ? (expense.amount / 100).toString() : '')
                  }
                  setValue={(v: string) => {
                    setAmountText(v)
                    // Comma accepted as decimal separator; unparseable text
                    // becomes NaN so isValidNumber blocks the save. Math.round
                    // avoids float cents (99.99 * 100 = 9998.999…).
                    const parsed = Number(v.replace(',', '.'))
                    dispatch({
                      type: 'amountChanged',
                      payload:
                        v.trim() === ''
                          ? 0
                          : Number.isFinite(parsed)
                          ? Math.round(parsed * 100)
                          : NaN,
                    })
                  }}
                  error={getFormFieldError('amount')}
                />
              </div>
              <div className="grid-column-12 g-col-lg-4">
                {!isLoadingOrganization ? (
                  <DropDownField
                    style={{}}
                    label="currency"
                    options={currencies.map((c) => {
                      return { label: `${c.name} (${c.symbol})`, value: c.code }
                    })}
                    value={expense.currencyCode}
                    onChange={(v) =>
                      dispatch({ type: 'currencyCodeChanged', payload: v })
                    }
                    loadingOptions={fetchCurrenciesStatus === 'pending'}
                    disabled={false}
                    inputRef={() => {}}
                  />
                ) : (
                  <ScreenLoading />
                )}
              </div>
            </div>
            <div className="grid">
              <div className="grid-column-12 g-col-lg-4">
                <AutoCompleteField
                  label="Category"
                  options={manualExpenseCategories}
                  value={expense.category}
                  onChange={(v) =>
                    dispatch({ type: 'categoryChanged', payload: v })
                  }
                  error={getFormFieldError('category')}
                />
              </div>
              <div className="grid-column-12 g-col-lg-4">
                <AutoCompleteField
                  label="Subcategory"
                  options={manualExpenseSubcategories}
                  value={expense.subcategory}
                  onChange={(v) =>
                    dispatch({ type: 'subcategoryChanged', payload: v })
                  }
                  error={getFormFieldError('subcategory')}
                />
              </div>
              <div className="grid-column-12 g-col-lg-4">
                <DropDownField
                  style={{}}
                  label="Is Marketing Related"
                  value={expense.isMarketingRelated ? 'yes' : 'no'}
                  onChange={(v) =>
                    dispatch({
                      type: 'isMarketingRelatedChanged',
                      payload: v === 'yes',
                    })
                  }
                  options={[
                    { label: 'Yes', value: 'yes' },
                    { label: 'No', value: 'no' },
                  ]}
                />
              </div>
            </div>
            <div className="grid">
              <div className="grid-column-12">
                <InputField
                  type="text"
                  label="Description"
                  value={expense.description}
                  setValue={(v) =>
                    dispatch({ type: 'descriptionChanged', payload: v })
                  }
                  error={getFormFieldError('description')}
                />
              </div>
            </div>
            <div className="section">
              <div className="advanced">
                <button
                  className="advanced-title link-button"
                  onClick={() => setAdvancedOpen(!advancedOpen)}
                >
                  Advanced
                  <span className="material-icons-outlined">
                    {advancedOpen ? 'expand_less' : 'expand_more'}
                  </span>
                </button>
                <div
                  className={advancedOpen ? 'collapse-open' : 'collapse-closed'}
                >
                  <div className="section-header">
                    <span className="section-title">Expense period</span>
                    <span className="subtitle-text">
                      Optional. Set both dates to split the amount evenly per
                      day from start to end, for example while a billboard is
                      on display. Leave both empty to report the whole amount
                      on the Payment Date.
                    </span>
                  </div>
                  <div className="grid">
                    <div className="grid-column-12 g-col-lg-6">
                      <InputField
                        type="date"
                        label="Period Start Date"
                        value={expense.startDate ? expense.startDate : ''}
                        setValue={(v) =>
                          dispatch({ type: 'startDateChanged', payload: v })
                        }
                        error={getFormFieldError('startDate')}
                        helperText="First day the cost is spread over."
                      />
                    </div>
                    <div className="grid-column-12 g-col-lg-6">
                      <InputField
                        type="date"
                        label="Period End Date"
                        value={expense.endDate ? expense.endDate : ''}
                        setValue={(v) =>
                          dispatch({ type: 'endDateChanged', payload: v })
                        }
                        error={getFormFieldError('endDate')}
                        helperText="Last day the cost is spread over. Must be after the start date."
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="grid-column-12">
          <div className="section actions">
            <div className="grid">
              <div className="grid-column-2">
                <div className="save-button">
                  {saveExpenseStatus === 'pending' ? (
                    <ScreenLoading />
                  ) : (
                    <>
                      <button onClick={handleSave} className="submit-button">
                        Save
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="grid-column-10" style={{ zIndex: -1 }}>
                {saveExpenseStatus === 'fulfilled' && (
                  <AnimatePresence>
                    <motion.span
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="material-icons-outlined saved-icon non-selectable"
                    >
                      task_alt
                    </motion.span>
                  </AnimatePresence>
                )}
                {saveExpenseError && (
                  <div className="saving-error">
                    {saveExpenseError}. Please try again later.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default React.memo(ManualExpenseView)
